import React, { useEffect, useState } from "react";
import {
  addAnswer,
  addNote,
  addWriteIn,
} from "../../../../redux/survey/actions";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  Checkbox,
  useMediaQuery,
  useTheme,
  Paper,
  Typography,
} from "@mui/material";
import { connect } from "react-redux";
import "rsuite/dist/styles/rsuite-default.css";
import "./GridType.css";

function GridType(props) {
  // Destructing  props for easy access to properties
  const { question, answeres, addAnswer_, isGreyOut, gridHideQuestions, hasError } =
    props;
  const theme = useTheme(); // Accessing the current theme
  const isMob = useMediaQuery(theme.breakpoints.down("sm")); // Check if device is mobile or not

  // Check if the input type is multiselect
  const isMultiselect = question.gridInputType === "multiselect";

  // State to manage grid answers
  // For radio: {gridRowId: optionId}
  // For multiselect: {gridRowId: [optionId1, optionId2, ...]}
  const [gridAnswers, setGridAnswers] = useState({});

  // Initialize state with proper format for the question type
  const initializeGridAnswers = () => {
    const existingAnswer = answeres[question._id];

    if (existingAnswer && existingAnswer.value) {
      // Restore both the grid answers and score exclusions
      const restoredState = {
        ...existingAnswer.value,
        grid_score_exclusions: existingAnswer.grid_score_exclusions || [],
      };
      setGridAnswers(restoredState);
    } else {
      // Initialize empty state based on input type
      const initialState = {};
      question.gridRows?.forEach((row) => {
        if (isMultiselect) {
          initialState[row._id] = []; // Empty array for multiselect
        } else {
          initialState[row._id] = null; // Null for radio
        }
      });
      setGridAnswers(initialState);
    }
  };

  // Load existing answers when component mounts or question changes
  useEffect(() => {
    initializeGridAnswers();
  }, [question._id, isMultiselect]);

  // Update Redux when gridAnswers changes
  useEffect(() => {
    // Extract grid_score_exclusions from gridAnswers
    const { grid_score_exclusions, ...actualAnswers } = gridAnswers;

    // Only update Redux if we have actual answers
    const hasAnswers = Object.values(actualAnswers).some((answer) => {
      if (isMultiselect) {
        return Array.isArray(answer) && answer.length > 0;
      } else {
        return answer !== null && answer !== undefined;
      }
    });

    if (hasAnswers) {
      addAnswer_({
        id: question._id,
        value: actualAnswers,
        type: "grid",
        grid_score_exclusions: grid_score_exclusions || [],
      });

      // Call Grid Hide Questions Function
      gridHideQuestions(actualAnswers, question?._id);
    }
  }, [gridAnswers]);

  // Function that handles radio button selection for each grid row
  const handleRadioSelection = (gridRowId, optionId, scoreExclude) => {
    if (isGreyOut) return;

    setGridAnswers((prev) => {
      // Ensure grid_score_exclusions is always an array
      const prevExclusions = Array.isArray(prev?.grid_score_exclusions)
        ? prev.grid_score_exclusions
        : [];

      let updatedExclusions;
      if (scoreExclude) {
        // Add gridRowId if not already present
        updatedExclusions = prevExclusions.includes(gridRowId)
          ? prevExclusions
          : [...prevExclusions, gridRowId];
      } else {
        // Remove gridRowId if present
        updatedExclusions = prevExclusions.filter((id) => id !== gridRowId);
      }

      return {
        ...prev,
        [gridRowId]: optionId,
        grid_score_exclusions: updatedExclusions,
      };
    });
  };

  // Function that handles checkbox selection for each grid row
  const handleCheckboxSelection = (gridRowId, optionId, scoreExclude) => {
    if (isGreyOut) return;

    setGridAnswers((prev) => {
      const currentSelections = prev[gridRowId] || [];
      let newSelections;

      if (currentSelections.includes(optionId)) {
        // Remove option if already selected
        newSelections = currentSelections.filter((id) => id !== optionId);
      } else {
        // Add option if not selected
        newSelections = [...currentSelections, optionId];
      }

      // Ensure grid_score_exclusions is always an array
      const prevExclusions = Array.isArray(prev?.grid_score_exclusions)
        ? prev.grid_score_exclusions
        : [];

      // Find all selected options for this grid row
      const selectedOptionIds = newSelections;

      // Find if any selected option for this row has score_exclusion_selection true
      const anyScoreExclude = question.options
        ?.filter((opt) => selectedOptionIds.includes(opt._id))
        ?.some((opt) => opt.score_exclusion_selection);

      let updatedExclusions;
      if (anyScoreExclude) {
        // Add gridRowId if not already present
        updatedExclusions = prevExclusions.includes(gridRowId)
          ? prevExclusions
          : [...prevExclusions, gridRowId];
      } else {
        // Remove gridRowId if present
        updatedExclusions = prevExclusions.filter((id) => id !== gridRowId);
      }

      return {
        ...prev,
        [gridRowId]: newSelections,
        grid_score_exclusions: updatedExclusions,
      };
    });
  };

  // Check if a specific option is selected for a row
  const isOptionSelected = (gridRowId, optionId) => {
    if (isMultiselect) {
      const selections = gridAnswers[gridRowId];
      return Array.isArray(selections) && selections.includes(optionId);
    }
    return gridAnswers[gridRowId] === optionId;
  };

  return (
    <Box className={isGreyOut ? "grid-disabled" : ""}>
      <div>
        <TableContainer
          component={Paper}
          className="grid-container"
          elevation={0}
          sx={{
            border: hasError ? "2px solid red !important" : undefined,
            borderRadius: hasError ? "4px" : undefined
          }}
        >
          <Table className="grid-table" size={isMob ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                {/* Empty cell for row headers */}
                <TableCell className="grid-header-cell">
                  {/* Empty header cell */}
                </TableCell>
                {/* Column headers for each option */}
                {question.options?.map((option) => (
                  <TableCell
                    key={option._id}
                    className="grid-header-cell"
                    style={{
                      color: option.textColor || "#404040",
                      minWidth: isMob ? "60px" : "80px",
                    }}
                  >
                    <Typography
                      variant={isMob ? "caption" : "body2"}
                      className="grid-header-text"
                    >
                      {option.text}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Row for each grid row item */}
              {question.gridRows?.map((gridRow) => (
                <TableRow key={gridRow._id}>
                  {/* Row header with the grid row text */}
                  <TableCell className="grid-row-header-cell">
                    <Typography
                      variant={isMob ? "caption" : "body2"}
                      className="grid-row-text"
                    >
                      {gridRow.text}
                    </Typography>
                  </TableCell>
                  {/* Input control for each option in this row */}
                  {question.options?.map((option) => (
                    <TableCell
                      key={option._id}
                      className="grid-body-cell"
                      style={{ cursor: isGreyOut ? "default" : "pointer" }}
                      onClick={() => {
                        if (isGreyOut) return;
                        if (isMultiselect) {
                          handleCheckboxSelection(
                            gridRow._id,
                            option._id,
                            option?.score_exclusion_selection
                          );
                        } else {
                          handleRadioSelection(
                            gridRow._id,
                            option._id,
                            option?.score_exclusion_selection
                          );
                        }
                      }}
                    >
                      {isMultiselect ? (
                        <Checkbox
                          className="grid-checkbox"
                          checked={isOptionSelected(gridRow._id, option._id)}
                          onClick={(e) => e.stopPropagation()} // <-- stop click bubbling
                          onChange={(e) => {
                            e.stopPropagation(); // prevent TableCell click
                            handleCheckboxSelection(
                              gridRow._id,
                              option._id,
                              option?.score_exclusion_selection
                            );
                          }}
                          value={option._id}
                          name={`grid-row-${gridRow._id}`}
                          disabled={isGreyOut}
                          size={isMob ? "small" : "medium"}
                          disableRipple
                        />
                      ) : (
                        <Radio
                          className="grid-radio-button"
                          checked={isOptionSelected(gridRow._id, option._id)}
                          onChange={() => {
                            handleRadioSelection(
                              gridRow._id,
                              option._id,
                              option?.score_exclusion_selection
                            );
                          }}
                          value={option._id}
                          name={`grid-row-${gridRow._id}`}
                          disabled={isGreyOut}
                          size={isMob ? "small" : "medium"}
                          disableRipple
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Box>
  );
}

// Function that extracts the necessary state from the Redux store
const mapStateToProps = (state) => {
  return {
    answeres: state.survey.answeres,
  };
};

// Function that binds the dispatch to action creators
const mapDispatchToProps = (dispatch) => {
  return {
    addAnswer_: (data) => dispatch(addAnswer(data)),
    addNote_: (data) => dispatch(addNote(data)),
    addWriteIn_: (data) => dispatch(addWriteIn(data)),
  };
};

// Connect Redux Store with this component
export default connect(mapStateToProps, mapDispatchToProps)(GridType);
